// ===== K-Tec AI Conversations Admin Page =====
// View and manage public AI conversations and leads

import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  MessageSquare, Phone, MessageCircle, User, Plus, ChevronRight,
  Bot, Filter, Search, Clock, CheckCircle, AlertCircle, TrendingUp,
  FileText, X, Eye,
} from 'lucide-react';
import { useAIStore, type AILead } from '../../ai/aiStore';

const STATUS_COLORS: Record<string, string> = {
  new: 'bg-blue-100 text-blue-700',
  'ai-chatting': 'bg-purple-100 text-purple-700',
  lead: 'bg-[#E8F2E3] text-[#4F7040]',
  'quotation-requested': 'bg-amber-100 text-amber-700',
  contacted: 'bg-sky-100 text-sky-700',
  converted: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-600',
};

const STATUS_LABELS: Record<string, string> = {
  new: 'New',
  'ai-chatting': 'AI Chatting',
  lead: 'Lead',
  'quotation-requested': 'Quotation Requested',
  contacted: 'Contacted',
  converted: 'Converted',
  closed: 'Closed',
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function ConversationDetailModal({ lead, conversation, onClose, onStatusChange }: {
  lead: AILead;
  conversation?: { messages: Array<{ id: string; role: string; content: string; timestamp: string }> };
  onClose: () => void;
  onStatusChange: (status: AILead['status']) => void;
}) {
  const statuses: AILead['status'][] = ['new', 'ai-chatting', 'lead', 'quotation-requested', 'contacted', 'converted', 'closed'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#F5F5F7]">
          <div>
            <h2 className="font-semibold text-[#1D1D1F]">{lead.customerName || 'Website Visitor'}</h2>
            <p className="text-sm text-[#86868B]">{lead.customerPhone || 'No phone'} · {lead.language}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-[#F5F5F7] transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Info bar */}
        <div className="px-6 py-3 bg-[#F5F5F7] flex flex-wrap gap-4 text-sm">
          <div>
            <span className="text-[#86868B]">Requirement: </span>
            <span className="text-[#1D1D1F] font-medium">{lead.requirement}</span>
          </div>
          {lead.estimatedValue && (
            <div>
              <span className="text-[#86868B]">Estimated: </span>
              <span className="text-[#7FA86A] font-semibold">LKR {lead.estimatedValue.toLocaleString()}</span>
            </div>
          )}
          <div>
            <span className="text-[#86868B]">Created: </span>
            <span className="text-[#1D1D1F]">{formatDate(lead.createdAt)}</span>
          </div>
        </div>

        {/* Chat history */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {conversation?.messages && conversation.messages.length > 0 ? (
            conversation.messages.map(msg => (
              <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} gap-2`}>
                {msg.role === 'assistant' && (
                  <div className="w-6 h-6 rounded-full bg-[#7FA86A] flex items-center justify-center flex-shrink-0 mt-1">
                    <Bot size={12} className="text-white" />
                  </div>
                )}
                <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${
                  msg.role === 'user'
                    ? 'bg-[#7FA86A] text-white rounded-tr-sm'
                    : 'bg-[#F5F5F7] text-[#1D1D1F] rounded-tl-sm'
                }`}>
                  <p className="whitespace-pre-wrap">{msg.content.substring(0, 500)}</p>
                  <p className="text-[10px] mt-1 opacity-60">{new Date(msg.timestamp).toLocaleTimeString()}</p>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-[#86868B]">
              <MessageSquare size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">No chat history available</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-[#F5F5F7]">
          <div className="flex flex-wrap gap-2 mb-3">
            <select
              value={lead.status}
              onChange={e => onStatusChange(e.target.value as AILead['status'])}
              className="px-3 py-2 text-sm border border-[#E5E5E7] rounded-xl focus:outline-none focus:border-[#7FA86A]"
            >
              {statuses.map(s => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap gap-2">
            {lead.customerPhone && (
              <>
                <a
                  href={`tel:${lead.customerPhone}`}
                  className="flex items-center gap-1.5 px-3 py-2 bg-[#F5F5F7] hover:bg-[#E5E5E7] rounded-xl text-sm font-medium transition-colors"
                >
                  <Phone size={14} /> Call
                </a>
                <a
                  href={`https://wa.me/94${lead.customerPhone.replace(/^0/, '')}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1.5 px-3 py-2 bg-green-50 hover:bg-green-100 text-green-700 rounded-xl text-sm font-medium transition-colors"
                >
                  <MessageCircle size={14} /> WhatsApp
                </a>
              </>
            )}
            <button className="flex items-center gap-1.5 px-3 py-2 bg-[#7FA86A] text-white rounded-xl text-sm font-medium hover:bg-[#658C53] transition-colors">
              <FileText size={14} /> Create Quotation
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default function AIConversationsPage() {
  const { leads, conversations, updateLeadStatus, aiNotifications, markAllAINotificationsRead } = useAIStore();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedLead, setSelectedLead] = useState<AILead | null>(null);

  // Stats
  const stats = {
    total: leads.length,
    leads: leads.filter(l => l.status === 'lead' || l.status === 'quotation-requested').length,
    converted: leads.filter(l => l.status === 'converted').length,
    totalValue: leads.reduce((s, l) => s + (l.estimatedValue || 0), 0),
  };

  const filtered = leads.filter(lead => {
    const matchSearch = !search ||
      lead.customerName?.toLowerCase().includes(search.toLowerCase()) ||
      lead.customerPhone?.includes(search) ||
      lead.requirement?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || lead.status === statusFilter;
    return matchSearch && matchStatus;
  }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const selectedConversation = selectedLead
    ? conversations.find(c => c.id === selectedLead.conversationId)
    : null;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1D1D1F]">AI Conversations</h1>
          <p className="text-[#86868B] text-sm mt-0.5">Website AI chat leads and conversations</p>
        </div>
        <button
          onClick={markAllAINotificationsRead}
          className="flex items-center gap-2 px-4 py-2 bg-[#F5F5F7] hover:bg-[#E5E5E7] rounded-xl text-sm font-medium transition-colors"
        >
          <CheckCircle size={15} /> Mark All Read
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Conversations', value: stats.total, icon: MessageSquare, color: 'text-blue-500 bg-blue-50' },
          { label: 'Active Leads', value: stats.leads, icon: TrendingUp, color: 'text-[#7FA86A] bg-[#F1F6EE]' },
          { label: 'Converted', value: stats.converted, icon: CheckCircle, color: 'text-green-500 bg-green-50' },
          { label: 'Est. Pipeline', value: `LKR ${(stats.totalValue / 1000).toFixed(0)}K`, icon: AlertCircle, color: 'text-purple-500 bg-purple-50' },
        ].map(stat => (
          <div key={stat.label} className="bg-white rounded-2xl p-4 shadow-sm border border-[#F5F5F7]">
            <div className={`w-9 h-9 rounded-xl ${stat.color} flex items-center justify-center mb-3`}>
              <stat.icon size={18} />
            </div>
            <div className="text-2xl font-bold text-[#1D1D1F]">{stat.value}</div>
            <div className="text-xs text-[#86868B] mt-0.5">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#86868B]" />
          <input
            type="text"
            placeholder="Search by name, phone, or requirement..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-white border border-[#E5E5E7] rounded-xl text-sm focus:outline-none focus:border-[#7FA86A]"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2.5 bg-white border border-[#E5E5E7] rounded-xl text-sm focus:outline-none focus:border-[#7FA86A]"
        >
          <option value="all">All Status</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {/* Leads list */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-[#F5F5F7]">
          <MessageSquare size={40} className="mx-auto mb-3 text-[#E5E5E7]" />
          <h3 className="font-semibold text-[#1D1D1F] mb-1">No conversations yet</h3>
          <p className="text-sm text-[#86868B]">AI chat conversations from the website will appear here</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(lead => (
            <motion.div
              key={lead.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-4 border border-[#F5F5F7] hover:border-[#7FA86A]/30 transition-colors cursor-pointer shadow-sm"
              onClick={() => setSelectedLead(lead)}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-[#7FA86A]/10 flex items-center justify-center flex-shrink-0">
                    <User size={18} className="text-[#7FA86A]" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#1D1D1F] text-sm">
                        {lead.customerName || 'Website Visitor'}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[lead.status]}`}>
                        {STATUS_LABELS[lead.status]}
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-[#F5F5F7] text-xs text-[#86868B]">
                        {lead.language}
                      </span>
                    </div>
                    <p className="text-sm text-[#86868B] mt-0.5 truncate">{lead.requirement}</p>
                    {lead.customerPhone && (
                      <p className="text-xs text-[#7FA86A] mt-0.5">{lead.customerPhone}</p>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  {lead.estimatedValue && (
                    <div className="text-sm font-semibold text-[#7FA86A]">
                      LKR {lead.estimatedValue.toLocaleString()}
                    </div>
                  )}
                  <div className="text-xs text-[#86868B] mt-0.5 flex items-center gap-1">
                    <Clock size={10} />
                    {formatDate(lead.createdAt)}
                  </div>
                  <ChevronRight size={14} className="text-[#86868B] ml-auto mt-1" />
                </div>
              </div>

              {/* Action buttons */}
              <div className="mt-3 flex gap-2 flex-wrap" onClick={e => e.stopPropagation()}>
                {lead.customerPhone && (
                  <>
                    <a
                      href={`tel:${lead.customerPhone}`}
                      className="flex items-center gap-1 px-2.5 py-1.5 bg-[#F5F5F7] hover:bg-[#E5E5E7] rounded-lg text-xs font-medium transition-colors"
                    >
                      <Phone size={11} /> Call
                    </a>
                    <a
                      href={`https://wa.me/94${lead.customerPhone.replace(/^0/, '')}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1 px-2.5 py-1.5 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg text-xs font-medium transition-colors"
                    >
                      <MessageCircle size={11} /> WhatsApp
                    </a>
                  </>
                )}
                <button className="flex items-center gap-1 px-2.5 py-1.5 bg-[#7FA86A]/10 hover:bg-[#7FA86A]/20 text-[#7FA86A] rounded-lg text-xs font-medium transition-colors">
                  <FileText size={11} /> Quotation
                </button>
                <button
                  className="flex items-center gap-1 px-2.5 py-1.5 bg-[#F5F5F7] hover:bg-[#E5E5E7] rounded-lg text-xs font-medium transition-colors"
                  onClick={() => setSelectedLead(lead)}
                >
                  <Eye size={11} /> View Chat
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Detail modal */}
      {selectedLead && (
        <ConversationDetailModal
          lead={selectedLead}
          conversation={selectedConversation as { messages: Array<{ id: string; role: string; content: string; timestamp: string }> } | undefined}
          onClose={() => setSelectedLead(null)}
          onStatusChange={status => {
            updateLeadStatus(selectedLead.id, status);
            setSelectedLead({ ...selectedLead, status });
          }}
        />
      )}
    </div>
  );
}
